import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
	IconButton, Button, Box, Dialog, DialogContent, DialogActions,
	Card, CardActionArea, CardContent, Typography, Tooltip, DialogTitle
} from '@mui/material';
import { TuneOutlined } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Clear';
import { useLanguage } from 'Context/LanguageContext';
import { SmartTitleTypography } from '~/FielderElements/SmartTaskTable/SmartStyles';
import { FielderHard, FielderPink } from '@core/theme/baseTheme';
import Grid from '@mui/material/Grid2';
import { StyledBadge } from '~/FielderElements/FielderElements.styles';
import SetupFunctionalityFilter from './SetupFunctionalityFilter';

const SetupDateFilter = (
	{
		 isLoading,
		 value,
		 onApplyPreset,
		 datePresets,
		 searchFunctionalities,
		 functionalityValue,
	 }) => {
	const lan = useLanguage();
	const cardTitles = useMemo(() => ({
		daily: lan.daily,
		weekly: lan.weekly,
		monthly: lan.monthly,
		annually: lan.annually,
		personalized: lan.customize,
	}), [lan.daily, lan.weekly, lan.monthly, lan.annually, lan.customize]);
	const descriptions = useMemo(() => ({
		daily: lan.thisDay || 'Use the current day.',
		weekly: lan.thisWeek || 'Use the current week.',
		monthly: lan.thisMonth || 'Use the current month.',
		annually: lan.thisYear || 'Use the current year.',
		personalized: lan.pickRange || 'Choose a custom range.',
	}), [lan.thisDay, lan.thisWeek, lan.thisMonth, lan.thisYear, lan.pickRange]);
	const cards = useMemo(() => ([
		{ value: datePresets.DAY, title: cardTitles.daily, description: descriptions.daily },
		{ value: datePresets.WEEK, title: cardTitles.weekly, description: descriptions.weekly },
		{ value: datePresets.MONTH, title: cardTitles.monthly, description: descriptions.monthly },
		{ value: datePresets.YEAR, title: cardTitles.annually, description: descriptions.annually },
		{ value: datePresets.CUSTOM, title: cardTitles.personalized, description: descriptions.personalized },
	]), [cardTitles.daily, descriptions.daily, cardTitles.weekly, descriptions.weekly, cardTitles.monthly,
		descriptions.monthly, cardTitles.annually, descriptions.annually, cardTitles.personalized, descriptions.personalized,
		datePresets.DAY, datePresets.WEEK, datePresets.MONTH, datePresets.YEAR, datePresets.CUSTOM]);
	const normalizeFunctionality = useCallback((valueToNormalize) => {
		if (typeof valueToNormalize === 'string' && valueToNormalize.trim()) return valueToNormalize;
		return null;
	}, []);
	const normalizedFunctionality = normalizeFunctionality(functionalityValue);
	const [openModal, setOpenModal] = useState(false);
	const [selectedValue, setSelectedValue] = useState(value);
	const [functionalityEnabled, setFunctionalityEnabled] = useState(() => normalizedFunctionality != null);
	const [selectedFunctionality, setSelectedFunctionality] = useState(() => (
		//return to searchFunctionalities.ALL
		normalizedFunctionality ?? searchFunctionalities.FORMS
	));
	useEffect(() => { setSelectedValue(value); }, [value]);
	useEffect(() => {
		const nextNormalized = normalizeFunctionality(functionalityValue);
		setFunctionalityEnabled(nextNormalized != null);
		setSelectedFunctionality(nextNormalized ?? searchFunctionalities.FORMS);//return to searchFunctionalities.ALL
	}, [functionalityValue, normalizeFunctionality, searchFunctionalities.FORMS]);//return to searchFunctionalities.ALL
	const handleOpen = () => setOpenModal(true);
	const handleClose = () => {
		setSelectedValue(value);
		const nextNormalized = normalizeFunctionality(functionalityValue);
		setFunctionalityEnabled(nextNormalized != null);
		setSelectedFunctionality(nextNormalized ?? searchFunctionalities.FORMS);//return to searchFunctionalities.ALL
		setOpenModal(false);
	};
	const handleToggleFunctionality = (enabled) => {
		setFunctionalityEnabled(enabled);
		if (enabled && !selectedFunctionality) setSelectedFunctionality(searchFunctionalities.FORMS);//return to searchFunctionalities.ALL
	};
	const handleFunctionalityChange = (functionality) => {
		setSelectedFunctionality(functionality);
	};
	const handleApply = () => {
		const nextFunctionality = functionalityEnabled ? (selectedFunctionality ?? searchFunctionalities.FORMS) : null;//return to searchFunctionalities.ALL
		onApplyPreset?.(selectedValue, nextFunctionality);
		setOpenModal(false);
	};
	const appliedFunctionality = normalizedFunctionality;
	const dateActive = value !== datePresets.CUSTOM;
	const functionalityActive = appliedFunctionality != null && appliedFunctionality !== searchFunctionalities.FORMS;//return to searchFunctionalities.ALL
	const showBadge = !isLoading && (dateActive || functionalityActive);
	const badgeCount = !isLoading ? (Number(dateActive) + Number(functionalityActive)) : 0;
	return (
		<>
			<Tooltip title={lan.configureSearchParameters}>
				<StyledBadge badgeContent={badgeCount} invisible={!showBadge}>
					<IconButton
						id="btn-setup-date-filter"
						onClick={handleOpen}
						disabled={isLoading}
						size="small">
						<TuneOutlined />
					</IconButton>
				</StyledBadge>
			</Tooltip>
			<Dialog
				open={openModal}
				onClose={handleClose}
				maxWidth={false}
				slotProps={{
					paper: {
						sx: {
							position: 'relative', overflow: 'visible', borderRadius: 2, minWidth: 500, maxWidth: '45vw', width: 'auto'
						},
					},
				}}
			>
				<IconButton
					onClick={handleClose}
					size="small"
					sx={{
						position: 'absolute', top: -8, right: -8, width: 24, height: 24, bgcolor: '#fff',
						zIndex: 10, boxShadow: 2, p: 0, '&:hover': { bgcolor: '#dc5c87' },
					}}
				>
					<CloseIcon fontSize="small" sx={{ color: '#dc5c87', '&:hover': { color: '#fff' } }} />
				</IconButton>
				<DialogTitle>
					<Box display="flex" justifyContent="flex-start">
						<TuneOutlined style={{ width: 25, height: 25, marginRight: 6, marginTop: 2 }} />
						<SmartTitleTypography variant={'subtitle1'}>
							{lan.configureSearchParameters}
						</SmartTitleTypography>
					</Box>
				</DialogTitle>
				<DialogContent dividers sx={{ p: 2 }}>
					<Grid container spacing={2}>
						{/* Cards column */}
						<Grid size={{ xs: 12, md: 9 }}>
							<Grid container spacing={2}>
								{cards.map((card) => {
									const active = selectedValue === card.value;
									return (
										<Grid key={String(card.value)} size={{ xs: 12, sm: 6 }}>
											<Card elevation={active ? 2 : 1}>
												<CardActionArea
													onClick={() => setSelectedValue(card.value)}
													data-active={active ? '' : undefined}
													sx={{
														height: '100%',
														'&[data-active]': {
															bgcolor: FielderHard,
															color: '#fff',
															outline: '2px solid',
															outlineColor: FielderPink,
														},
													}}
												>
													<CardContent sx={{ height: '100%' }}>
														<Typography variant="h6" gutterBottom>{card.title}</Typography>
														<Typography variant="body2">{card.description}</Typography>
													</CardContent>
												</CardActionArea>
											</Card>
										</Grid>
									);
								})}
							</Grid>
						</Grid>
						{/* Radio column */}
						<Grid size={{ xs: 12, md: 3 }}>
							<SetupFunctionalityFilter
								lan={lan}
								searchFunctionalities={searchFunctionalities}
								selectedFunctionality={selectedFunctionality}
								onFunctionalityChange={handleFunctionalityChange}
								enabled={functionalityEnabled}
								onToggle={handleToggleFunctionality}
							/>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions sx={{ justifyContent: 'flex-end', px: 1, py: 1 }}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
						<Button variant="contained" onClick={handleApply} size="small" sx={{ backgroundColor: '#dc5c87' }}>
							{lan.apply}
						</Button>
					</Box>
				</DialogActions>
			</Dialog>
		</>
	);
};
export default SetupDateFilter;
